import { ActionsRequestMessageSchema, type LLMProvider, type Message } from '../types';
import { generateNextQuestions } from '../userResponse/next-questions';
import { logger } from '../utils/logger';
import { ThreadManager } from '../threads';
import { CONTEXT_CONFIG } from '../config/context';

/**
 * Handle incoming actions messages from runtime
 * Generates suggested next questions based on the original user prompt and generated component
 */
export async function handleActionsRequest(
  data: any,
  sendMessage: (message: Message) => void,
  anthropicApiKey?: string,
  groqApiKey?: string,
  geminiApiKey?: string,
  openaiApiKey?: string,
  llmProviders?: LLMProvider[]
): Promise<void> {
  try {
    logger.debug('[ACTIONS_REQ] Parsing incoming actions request');
    const actionsRequest = ActionsRequestMessageSchema.parse(data);
    const { id, payload } = actionsRequest;
    const { SA_RUNTIME } = payload;

    const wsId = actionsRequest.from.id || 'unknown';

    logger.info(`[ACTIONS_REQ ${id}] Processing actions request from client: ${wsId}`);
    logger.debug(`[ACTIONS_REQ ${id}] Request payload:`, JSON.stringify(payload, null, 2).substring(0, 200));

    // SA_RUNTIME is required to fetch actions from UIBlock
    if (!SA_RUNTIME) {
      logger.error(`[ACTIONS_REQ ${id}] SA_RUNTIME missing from request`);
      sendResponse(id, {
        success: false,
        error: 'SA_RUNTIME with threadId and uiBlockId is required'
      }, sendMessage, wsId);
      return;
    }

    const uiBlockId = SA_RUNTIME.uiBlockId;
    const threadId = SA_RUNTIME.threadId;

    logger.debug(`[ACTIONS_REQ ${id}] SA_RUNTIME validated - threadId: ${threadId}, uiBlockId: ${uiBlockId}`);

    // Get UIBlock from ThreadManager
    const threadManager = ThreadManager.getInstance();
    const thread = threadManager.getThread(threadId);

    if (!thread) {
      logger.error(`[ACTIONS_REQ ${id}] Thread '${threadId}' not found`);
      sendResponse(id, {
        success: false,
        error: `Thread '${threadId}' not found`
      }, sendMessage, wsId);
      return;
    }

    logger.debug(`[ACTIONS_REQ ${id}] Thread found with ${thread.getUIBlocks().length} UIBlocks`);
    logger.debug(`[ACTIONS_REQ ${id}] Retrieving UIBlock: ${uiBlockId}`);

    const uiBlock = thread.getUIBlock(uiBlockId);
    if (!uiBlock) {
      logger.error(`[ACTIONS_REQ ${id}] UIBlock '${uiBlockId}' not found in thread '${threadId}'`);
      sendResponse(id, {
        success: false,
        error: `UIBlock '${uiBlockId}' not found in thread '${threadId}'`
      }, sendMessage, wsId);
      return;
    }

    logger.info(`[ACTIONS_REQ ${id}] UIBlock retrieved successfully`);

    // Extract data from UIBlock
    logger.debug(`[ACTIONS_REQ ${id}] Extracting data from UIBlock`);
    const userQuestion = uiBlock.getUserQuestion();
    const component = uiBlock.getComponentMetadata();
    const componentData = uiBlock.getComponentData();

    logger.debug(`[ACTIONS_REQ ${id}] User question: "${userQuestion?.substring(0, 50)}..."`);
    logger.debug(`[ACTIONS_REQ ${id}] Component: ${component?.name || 'unknown'} (${component?.type || 'unknown'})`);
    logger.debug(`[ACTIONS_REQ ${id}] Component data available: ${componentData ? 'yes' : 'no'}`);

    // Extract conversation context from thread (excluding current UIBlock)
    logger.debug(`[ACTIONS_REQ ${id}] Extracting conversation history (max ${CONTEXT_CONFIG.MAX_CONVERSATION_CONTEXT_BLOCKS} blocks)`);
    const conversationHistory = thread.getConversationContext(CONTEXT_CONFIG.MAX_CONVERSATION_CONTEXT_BLOCKS, uiBlockId);
    const historyLineCount = conversationHistory.split('\n').filter(l => l.trim()).length;
    logger.info(`[ACTIONS_REQ ${id}] Conversation history extracted: ${historyLineCount} lines`);
    logger.debug(`[ACTIONS_REQ ${id}] Conversation history preview:\n${conversationHistory.substring(0, 200)}...`);

    logger.info(`[ACTIONS_REQ ${id}] Generating actions for UIBlock: ${uiBlockId}, component: ${component?.name || 'unknown'}`);

    // Use getOrFetchActions to manage action state
    logger.debug(`[ACTIONS_REQ ${id}] Checking if actions are already cached`);
    const startTime = Date.now();

    const actions = await uiBlock.getOrFetchActions(async () => {
      logger.info(`[ACTIONS_REQ ${id}] Actions not cached, generating new actions...`);

      // Generate next questions using extracted data from UIBlock and conversation history
      logger.info(`[ACTIONS_REQ ${id}] Starting next questions generation with ${llmProviders?.join(', ') || 'default'} providers`);
      const nextQuestions = await generateNextQuestions(
        userQuestion,
        component as any,
        componentData,
        anthropicApiKey,
        groqApiKey,
        geminiApiKey,
        openaiApiKey,
        llmProviders,
        conversationHistory
      );

      logger.info(`[ACTIONS_REQ ${id}] Generated ${nextQuestions.length} questions`);
      logger.debug(`[ACTIONS_REQ ${id}] Questions: ${JSON.stringify(nextQuestions)}`);

      // Convert questions to actions format
      logger.debug(`[ACTIONS_REQ ${id}] Converting questions to actions format`);
      const convertedActions = nextQuestions.map((question: string, index: number) => ({
        id: `action_${index}_${Date.now()}`,
        name: question,
        type: 'next_question',
        question
      }));

      logger.debug(`[ACTIONS_REQ ${id}] Converted ${convertedActions.length} actions`);
      return convertedActions;
    });

    const processingTime = Date.now() - startTime;
    logger.info(`[ACTIONS_REQ ${id}] Actions retrieved in ${processingTime}ms - ${actions.length} actions total`);

    if (actions.length > 0) {
      logger.info(`[ACTIONS_REQ ${id}] Generated ${actions.length} follow-up questions successfully`);
      logger.debug(`[ACTIONS_REQ ${id}] Actions: ${actions.map(a => a.name).join(', ')}`);
    } else {
      logger.warn(`[ACTIONS_REQ ${id}] No actions generated`);
    }

    logger.debug(`[ACTIONS_REQ ${id}] Sending successful response to client`);
    sendResponse(id, {
      success: true,
      data: {
        actions,
        componentName: component?.name,
        componentId: component?.id,
        uiBlockId,
        threadId
      }
    }, sendMessage, wsId);

    logger.info(`[ACTIONS_REQ ${id}] ✓ Actions request completed successfully`);

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    const errorStack = error instanceof Error ? error.stack : undefined;

    logger.error(`[ACTIONS_REQ] Failed to handle actions request: ${errorMessage}`);
    logger.debug(`[ACTIONS_REQ] Error stack trace:`, errorStack);

    sendResponse(null, {
      success: false,
      error: errorMessage
    }, sendMessage);

    logger.info('[ACTIONS_REQ] ✗ Actions request completed with errors');
  }
}

/**
 * Send actions response
 */
function sendResponse(
  id: string | null,
  res: { success: boolean; error?: string; data?: any },
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id: id || 'unknown',
    type: 'ACTIONS_RES',
    from: { type: 'data-agent' },
    to: {
      type: 'runtime',
      id: clientId
    },
    payload: {
      ...res,
    }
  };

  logger.debug(`[ACTIONS_RES ${id || 'unknown'}] Sending ${res.success ? 'successful' : 'failed'} response to client`);
  logger.debug(`[ACTIONS_RES ${id || 'unknown'}] Response payload size: ${JSON.stringify(response).length} bytes`);

  if (res.data?.actions) {
    logger.debug(`[ACTIONS_RES ${id || 'unknown'}] Sending ${res.data.actions.length} actions`);
  }

  sendMessage(response);
}
